import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { State, Selector } from "./types";
import { initialState } from "./initialState";
import { createSelector } from "./selector";

function handleRequest(
  state: State,
  { elementId }: Boundaries["retrieveElementConstraints"]["request"],
): State {
  const index = state.findIndex((req) => elementId === req.elementId);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { elementId, constraints: [], request: setRequestStateToPending() },
      ];
}

function handleSuccess(
  state: State,
  {
    elementId,
    constraints,
  }: Boundaries["retrieveElementConstraints"]["success"],
): State {
  const index = state.findIndex((req) => elementId === req.elementId);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], constraints, request: setRequestStateToSuccess() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { elementId, constraints, request: setRequestStateToSuccess() },
      ];
}

function handleFailure(
  state: State,
  { elementId }: Boundaries["retrieveElementConstraints"]["failure"],
): State {
  const index = state.findIndex((req) => elementId === req.elementId);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { elementId, request: setRequestStateToFailure(), constraints: [] },
      ];
}

function handleEditConstraint(
  state: State,
  { constraint }: Boundaries["editConstraint"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    constraints: req.constraints.map((c) =>
      c.uuid === constraint.uuid ? { ...c, ...constraint } : c,
    ),
  }));
}

function handleRemoveConstraint(
  state: State,
  constraint: Boundaries["removeConstraint"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    constraints: req.constraints.filter((c) => c.uuid !== constraint.uuid),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveElementConstraintsRequested]: handleRequest,
    [events.retrieveElementConstraintsSucceeded]: handleSuccess,
    [events.retrieveElementConstraintsFailed]: handleFailure,
    [events.editConstraintSucceeded]: handleEditConstraint,
    [events.removeConstraintSucceeded]: handleRemoveConstraint,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
